/* The consultation workspace (DESIGN.md §6.3, §8; plan §7.1, P9). The note and the right
   column sit side by side whenever the content area is at least 880px wide (1366 × 768 and
   1280 × 720 with the full nav; 1024 × 768 once the nav collapses), and the right column —
   diagnoses, orders and results, prescriptions — stays in view while the note scrolls.
   Narrower, the right column moves under the note. Widths are the content area's, not the
   window's, so the layout follows the nav. */
.page {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  min-width: 0;
  container: workspace / inline-size;
}

.actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2) var(--space-3);
}

.vitals {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: var(--space-1) var(--space-4);
  margin: 0;
  padding: var(--space-3) var(--space-4);
  border: 1px solid var(--border);
  border-radius: var(--radius-card);
  background: var(--paper);
  font: var(--t-body);
  font-variant-numeric: tabular-nums;
}

.vitalsLabel {
  font: var(--t-body-strong);
}

.vital {
  white-space: nowrap;
}

.vital[data-abnormal] {
  font-weight: 600;
}

.vital[data-critical] {
  color: var(--danger);
  font-weight: 600;
}

.columns {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: var(--space-6);
  align-items: start;
}

.column {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
  min-width: 0;
}

@container workspace (width >= 880px) {
  .columns {
    grid-template-columns: minmax(0, 55fr) minmax(0, 45fr);
    gap: var(--space-6);
  }

  /* The right column is the clinician's context while writing: it stays below the sticky
     patient banner (88px) and scrolls on its own when it is taller than the window. */
  .side {
    position: sticky;
    top: calc(var(--topbar-height) + 88px + var(--space-4));
    max-height: calc(100vh - var(--topbar-height) - 88px - var(--space-5));
    padding-right: var(--space-2);
    overflow-y: auto;
    overscroll-behavior: contain;
  }
}

/* A short window can't hold a sticky column worth reading: let it scroll with the page. */
@media (height < 640px) {
  .side {
    position: static;
    max-height: none;
    overflow: visible;
  }
}

.panel {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  min-width: 0;
}

.heading {
  margin: 0;
  font: var(--t-heading);
}

.subheading {
  margin: 0;
  font: var(--t-body-strong);
}

.help {
  margin: 0;
  color: var(--graphite);
  font: var(--t-small);
}

.list {
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  list-style: none;
}

.item {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--space-1) var(--space-3);
  min-height: 44px;
  padding: var(--space-2) 0;
  border-bottom: 1px solid var(--rule);
}

.item[data-inactive] {
  color: var(--pencil);
}

.itemMain {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  min-width: 0;
}

.itemTitle {
  font: var(--t-body-strong);
}

.itemMeta {
  color: var(--graphite);
  font: var(--t-small);
}

.code {
  margin-right: var(--space-2);
}

/* The patient summary: one row of four short sections on a laptop, two on a tablet, one on a
   phone. Each is a ui Section, so its surface comes from the design system. */
.summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(14rem, 1fr));
  gap: var(--space-4) var(--space-5);
}

.form {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: var(--space-4);
  border: 1px solid var(--rule);
  border-radius: var(--radius-control);
}

.results {
  width: 100%;
  border-collapse: collapse;
  font-variant-numeric: tabular-nums;
}

.results th,
.results td {
  height: var(--row-height-dense);
  padding: 0 var(--space-2);
  border-bottom: 1px solid var(--rule);
  font: var(--t-small);
  text-align: left;
}

.results th {
  color: var(--graphite);
  font-weight: 600;
}

.results td[data-abnormal] {
  font-weight: 600;
}

.results td[data-critical] {
  color: var(--danger);
  font-weight: 600;
}

.slot {
  padding: var(--space-4);
  border: 1px dashed var(--rule);
  color: var(--graphite);
  font: var(--t-small);
}

.history {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.historyVersion {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  padding-bottom: var(--space-3);
  border-bottom: 1px solid var(--rule);
}

.signed {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.signedSection {
  margin: 0;
}

.signedSection dt {
  font: var(--t-body-strong);
}

.signedSection dd {
  margin: 0;
  font: var(--t-body);
  line-height: 1.55;
  white-space: pre-wrap;
}
